import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { ChangeEvent, useContext, useRef, useState } from "react";
import CircularProgress from '@mui/material/CircularProgress';
import useDealBuilderAlertsStore from "../stores/deal-builder-alerts.store.ts";
import useDealBuilderResultStore from "../stores/deal-builder-result.store.ts";
import _ from "lodash";
import usePricingManagementStore from "../stores/pricing-management.store.ts";
import useDealBuilderSaver from "./hooks/useDealBuilderSaver.ts";
import usePdfExtraction from "./hooks/usePdfExtraction.ts";
import useBuildDeal from "./hooks/useBuildDeal.ts";
import useDealBuilderExtractionStore from "../stores/deal-builder-extraction.store.ts";
import { UserInfoContext } from "../contexts/UserInfoContext.js";

export default function DealBuilderButtonPanel() {
  const { setDealBuilderService, DealBuilderService, setResetCopy, resetCopy } = useDealBuilderResultStore();
  const { loading: pmLoading } = usePricingManagementStore();
  const { buildDeal } = useBuildDeal();
  const { setSnackMessage } = useDealBuilderAlertsStore();
  const { saveDealAsNew } = useDealBuilderSaver();
  const { extractFromPdf } = usePdfExtraction();
  const { warnings, sourceFileName, extracting, clearExtraction, dismissWarnings } = useDealBuilderExtractionStore();
  const { userInfo } = useContext(UserInfoContext);
  const pdfInputRef = useRef<HTMLInputElement>(null);
  const [clearDialog, setClearDialog] = useState<boolean>(false);
  const [duplicateDialog, setDuplicateDialog] = useState<boolean>(false);

  async function onSaveButton() {
    console.log("1. Save button clicked");
    if (!DealBuilderService) return;

    console.log("Current issuance title:", DealBuilderService.issuanceTitle);

    const titleChanged = DealBuilderService.isSaved &&
      resetCopy?.issuanceTitle !== undefined &&
      DealBuilderService.issuanceTitle !== resetCopy.issuanceTitle;

    console.log("Title changed:", titleChanged);

    if (titleChanged) {
      setDuplicateDialog(true);
      return;
    }

    await performSave();
  }

  async function performSave() {
    if (!DealBuilderService) return;
    setDuplicateDialog(false);
    setResetCopy(_.cloneDeep(DealBuilderService));
    const result = await DealBuilderService.save();
    if (!result.valid) setSnackMessage(result.error);
  }

  async function onSaveAsDuplicate() {
    if (!DealBuilderService) return;
    setDuplicateDialog(false);
    const newDeal = await saveDealAsNew(DealBuilderService, DealBuilderService.dealStatus);
    newDeal.isSaved = true;
    setDealBuilderService(newDeal);
    setResetCopy(_.cloneDeep(newDeal));
  }

  function onClearDealButton() {
    setClearDialog(true);
  }

  function onClearConfirmation() {
    setClearDialog(false);
    setDealBuilderService(undefined);
    clearExtraction();
  }

  async function onPdfFileSelected(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    const result = await extractFromPdf(file);
    if (!result.valid) return setSnackMessage(result.error);

    const summary = result.body.maturities > 0 ?
      `Imported ${result.body.maturities} maturities from ${file.name}.` :
      `Imported deal details from ${file.name}.`;
    setSnackMessage(summary);

    // Build the deal right away with the imported values.
    await buildDeal();
  }

  return (
    <Stack direction="column" gap={1} sx={{
      background: "white",
      borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
      borderRight: "1px solid rgba(0, 0, 0, 0.12)",
      p: 1,
    }}>

      {!DealBuilderService && (
        <>
          <Button
            variant="contained"
            sx={{ color: "white", height: "32px" }}
            onClick={() => buildDeal()}
            disabled={pmLoading || extracting}
          >Build Deal</Button>

          {(userInfo as any)?.canExtractPdf && (
            <>
              <Button
                variant="outlined"
                sx={{ height: "32px" }}
                onClick={() => pdfInputRef.current?.click()}
                disabled={pmLoading || extracting}
                startIcon={
                  extracting ? <CircularProgress size={16} color="inherit" /> : null
                }
              >{extracting ? "Importing..." : "Import from PDF"}</Button>

              <input
                ref={pdfInputRef}
                type="file"
                accept="application/pdf"
                style={{ display: "none" }}
                onChange={onPdfFileSelected}
              />
            </>
          )}
        </>
      )}

      <Dialog
        open={warnings.length > 0}
        onClose={dismissWarnings}
      >
        <DialogTitle>
          PDF import notes
        </DialogTitle>
        <DialogContent sx={{ minWidth: "400px", maxWidth: "600px" }}>
          <ul style={{ margin: 0, paddingLeft: "20px" }}>
            {warnings.map((warning, index) => (
              <li key={index}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>{warning}</Typography>
              </li>
            ))}
          </ul>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={dismissWarnings}>Close</Button>
        </DialogActions>
      </Dialog>

      {DealBuilderService && (
        <Stack direction="row" gap={1}>
          <Button
            variant="contained"
            sx={{ color: "white", flex: 1, height: "32px" }}
            onClick={() => onSaveButton()}
            disabled={pmLoading}
            startIcon={
              pmLoading ? <CircularProgress size={16} color="inherit" /> : null
            }
          >Save</Button>

          <Button
            variant="contained"
            sx={{ color: "white", flex: 1, height: "32px" }}
            color="warning"
            onClick={onClearDealButton}
            disabled={pmLoading}
          >Clear</Button>
        </Stack>
      )}

      <Dialog
        open={clearDialog}
        onClose={() => setClearDialog(false)}
      >
        <DialogTitle>Clear deal</DialogTitle>
        <DialogContent sx={{ minWidth: "400px" }}>
          <Typography variant="subtitle2">
            If you clear the current deal you will lose all unsaved changes.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="error" onClick={() => setClearDialog(false)}>Cancel</Button>
          <Button variant="outlined" onClick={onClearConfirmation}>Accept</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={duplicateDialog}
        onClose={() => setDuplicateDialog(false)}
      >
        <DialogTitle>Issuance title changed</DialogTitle>
        <DialogContent sx={{ minWidth: "400px" }}>
          <Typography variant="subtitle2">
            The issuance title has changed. Do you want to overwrite the existing deal or save it as a new duplicate?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="error" onClick={() => setDuplicateDialog(false)}>Cancel</Button>
          <Button variant="outlined" onClick={performSave}>Save</Button>
          <Button variant="contained" sx={{ color: "white" }} onClick={onSaveAsDuplicate}>Save as Duplicate</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
